import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import InlineSVG from "react-inlinesvg";
import TextareaAutosize from "react-textarea-autosize";
import { StyleSheetManager } from "styled-components";
import { COMMAND_MENU_HIGH_QUALITY, COMMAND_MENU_NORMAL, MAX_QUICK_EDIT_PRECEDING_TEXT_WORDS } from "../../../../../src/shared/constants";
import Auth from "../../../Auth";
import { useAppStore } from "../../../stores/appStore";
import { useProjectStore } from "../../../stores/projectStore";
import { useSudowriteStore } from "../../../stores/sudowriteStore";
import IconButton from "../../IconButton";
import Toggle from "../../Toggle";
import Thumbs from "../Thumbs";
import { convertQuotes } from "../../../lib/convertQuotes";
import { splitSingleNewlines } from "../Utils";
import { Container, DotContainer } from "./styles";
import { handleStreamError, getStreamErrorMessage } from "./handleStreamError";

export const AnimatedDots = () => {
  return (
    <DotContainer>
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="dot"
        />
      ))}
    </DotContainer>
  );
};

const QuickEditButton = ({ disabled }) => {
  return (
    <button
      type="submit"
      className={classNames({ disabled })}
      aria-label="Submit quick edit instructions"
    >
      Edit
      <InlineSVG
        src="/assets/keyboard-shortcuts/enter-key.svg"
        className="keyboard-shortcut"
        aria-label="Press Enter to submit quick edit instructions"
      />
    </button>
  );
};

const EditChoices = ({ handleCloseAccept, handleCloseReject, invocationId }) => {
  const setFeedbackOpen = useProjectStore((state) => state.setFeedbackOpen);
  const setFeedbackInvocation = useProjectStore((state) => state.setFeedbackInvocation);
  const isMac = useAppStore((state) => state.isMac);

  return (
    <div className="edit-choices">
      <Thumbs
        invocationId={invocationId}
        setFeedbackInvocation={setFeedbackInvocation}
        setFeedbackOpen={setFeedbackOpen}
      />
      <button
        onClick={handleCloseReject}
        aria-label="Reject quick edit changes"
      >
        Reject
        <InlineSVG
          src="/assets/keyboard-shortcuts/escape-key.svg"
          className="keyboard-shortcut"
          aria-label="Press Escape to reject quick edit changes"
        />
      </button>
      <button
        onClick={handleCloseAccept}
        aria-label="Accept quick edit changes"
      >
        Accept
        <div
          className="keyboard-shortcut-container"
          aria-label={`Press ${isMac ? "command" : "control"} plus enter to accept quick edit changes`}
        >
          <InlineSVG
            src={isMac ? "/assets/keyboard-shortcuts/command-key.svg" : "/assets/keyboard-shortcuts/control-key-pc.svg"}
            className="keyboard-shortcut"
            aria-hidden="true"
          />
          <InlineSVG
            src="/assets/keyboard-shortcuts/enter-key.svg"
            className="keyboard-shortcut"
            aria-hidden="true"
          />
        </div>
      </button>
    </div>
  );
};

const CommandMenu = ({
  tiptap, // the tiptap editor instance
  dismiss,
  didFinalizeChangesRef: externalDidFinalizeChangesRef,
  editor, // editor class wraps tiptap editor (see Editor.js)
  fontSize, // font size of text within the editor as set by the user
  node, // #editor element within the container
}) => {
  const [instruction, setInstruction] = useState("");
  const [instructions, setInstructions] = useState([]);
  const [editSubmitted, setEditSubmitted] = useState(false);
  const [followUp, setFollowUp] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [editError, setEditError] = useState(null);

  const inputRef = useRef(null);
  const followUpRef = useRef(null);
  const containerRef = useRef(null);
  const inovcationRef = useRef(null);
  const localDidFinalizeChangesRef = useRef(false);
  const didFinalizeChangesRef = externalDidFinalizeChangesRef || localDidFinalizeChangesRef;
  // Per-mount track-changes session id. Scoping accept/reject to this id ensures Quick Edit
  // never touches tracked marks that belong to other flows or earlier edits in the same doc.
  const editSessionIdRef = useRef(`qe-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  const document = useProjectStore((state) => state.document);
  const userData = useSudowriteStore((state) => state.userData);
  const [highQuality, setHighQuality] = useState(userData?.data?.commandMenuMode?.value === COMMAND_MENU_HIGH_QUALITY.value);

  const domHost = node?.ownerDocument?.body || document.body; // fallback to document.body if node is undefined

  useEffect(() => {
    const sessionId = editSessionIdRef.current;
    didFinalizeChangesRef.current = false;

    if (tiptap && !tiptap.isDestroyed) {
      try {
        tiptap.commands.setEditSessionId(sessionId);
      } catch (e) {
        Sentry.captureException(e, {
          tags: {
            documentId: document.id,
            func: "setEditSessionId",
          },
        });
      }
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }

    return () => {
      if (!tiptap || tiptap.isDestroyed) {
        return;
      }

      try {
        tiptap.commands.setTrackChangeStatus(false);
      } catch (e) {
        Sentry.captureException(e, {
          tags: {
            documentId: document.id,
            func: "setTrackChangeStatus",
          },
        });
      }

      if (didFinalizeChangesRef.current) {
        return;
      }

      try {
        tiptap.commands.rejectChangesBySession(sessionId);
      } catch (e) {
        Sentry.captureException(e, {
          tags: {
            documentId: document.id,
            func: "rejectChangesBySession",
          },
        });
      }
    };
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Focus the follow-up box when an edit finishes streaming. The textarea only
  // mounts once editSubmitted commits, which under React 18 happens after the
  // stream's onEnd callback returns — so it can't be focused inline there.
  useEffect(() => {
    if (editSubmitted && !streaming) {
      followUpRef.current?.focus();
    }
  }, [editSubmitted, streaming]);

  useEffect(() => {
    if (userData) {
      userData.update({ commandMenuMode: highQuality ? COMMAND_MENU_HIGH_QUALITY : COMMAND_MENU_NORMAL });
    }
  }, [highQuality]);

  // ### OVERLAY POSITIONING ###

  const getPosition = () => {
    const selectionBounds = editor.getHighlightCoords();
    const containerHeight = containerRef.current ? containerRef.current.getBoundingClientRect().height : 0;

    let top = selectionBounds?.top || 0;

    let topCalc = top - fontSize - 250 / fontSize - containerHeight;

    if (topCalc < 0) {
      top = selectionBounds?.bottom + 10 || 10;

      // Extend #editor (the menu's containing block, which clips overflow)
      // so the menu isn't hidden at the end of the document — replaces the
      // old iframe-height hack. Growing #editor grows the container content,
      // so scrolling keeps working in both regular and focus mode.
      const menuBottom = top + containerHeight + 250;
      const editorHeightNow = node.getBoundingClientRect().height;

      if (editorHeightNow <= menuBottom) {
        node.style.minHeight = `${menuBottom}px`;
      }
    }
    top = topCalc < 0 ? selectionBounds?.bottom + 10 || 10 : topCalc;
    return top;
  };

  const topRef = useRef(getPosition());

  useEffect(() => {
    if (!streaming) {
      topRef.current = getPosition();
    }
  }, [streaming]);

  // Release any #editor min-height extension applied by getPosition() when
  // the menu closes, so the editor returns to content-driven height.
  useEffect(() => {
    return () => {
      if (node?.style?.minHeight) {
        node.style.minHeight = "";
      }
    };
  }, [node]);

  const overlayStyle = {
    position: "absolute",
    top: `${topRef.current}px`,
    width: "calc(100% - 70px)",
    left: "17px",
  };

  const selectInserted = ({ clearSelection = true } = {}) => {
    const nodeList = node.querySelectorAll("insert");

    if (nodeList.length === 0) return;

    const firstNode = nodeList[0];
    const lastNode = nodeList[nodeList.length - 1];

    const { from } = editor.getPosFromNode(firstNode);
    const { to } = editor.getPosFromNode(lastNode);

    editor.highlight(from, to, true, { className: "quick-edit-inserted-highlight", sticky: true, clearSelection });
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setEditError(null);
    setStreaming(true);

    // const selection = getSelection();
    // const passage = selection.text;

    const range = editor.getHighlightedText();
    const passage = range.text.trim() === "" ? null : range.text;
    let precedingText = editor.getPrecedingText(MAX_QUICK_EDIT_PRECEDING_TEXT_WORDS); // 1k words

    // Check if preceding text is blank
    if (precedingText.trim() === "") {
      precedingText = null;
    }

    if (!followUp) {
      setInstructions(instructions.concat([instruction]));
    } else {
      setInstructions(instructions.concat([followUp]));
      tiptap.commands.deleteRange({ from: range.from, to: range.to });
    }

    const fullInstruction = `${instruction} ${followUp}`;

    tiptap.setOptions({ editable: false });

    tiptap.commands.setTrackChangeStatus(true);

    if (!followUp) {
      try {
        tiptap.commands.deleteRange({ from: range.from, to: range.to });
      } catch (transformErr) {
        tiptap.commands.setTrackChangeStatus(false);
        tiptap.setOptions({ editable: true });
        setEditError("Something went wrong preparing this edit. Please try again.");
        setStreaming(false);
        Sentry.captureException(transformErr, {
          tags: {
            documentId: document.id,
            func: "handleSubmitEdit-deleteRange",
          },
        });
        return;
      }
    }
    tiptap.commands.focus(range.from);

    setFollowUp("");

    // determine if the high qyality toggle is on

    let endpoint;

    if (highQuality) {
      endpoint = "highQuality";
    } else {
      endpoint = "normal";
    }

    const url = `/api/ai/runDynamicPluginAction/commandMenuPlugins/instruct/${endpoint}`;
    const pluginId = `commandMenuPlugins/instruct/${endpoint}`;

    await Auth.streamWithIdToken(
      url,
      {
        pluginId,
        params: {
          documentId: document.id,
          instruction: fullInstruction,
          passage,
          preceding_text: precedingText,
        },
      },
      // abortController.current.signal,
      null,
      (chunk) => {
        if (chunk?.content?.text) {
          const textCopy = chunk.content.text;
          const chunkText = convertQuotes(chunk.content.text, tiptap);
          const paragraphs = splitSingleNewlines(chunkText);

          paragraphs.forEach((paragraph) => {
            tiptap.commands.insertContent(paragraph);
            if (textCopy.includes("\n\n")) {
              tiptap.commands.splitBlock();
            }
          });

          // Keep the inserted text green as it streams in (matching legacy
          // behavior). Preserve the cursor so the next chunk inserts correctly.
          !tiptap.isDestroyed && selectInserted({ clearSelection: false });
        }

        if (chunk?.invocationId) {
          inovcationRef.current = chunk.invocationId;
        }
      },
      // onEnd
      () => {
        // end on a newline
        tiptap.commands.insertContent([
          {
            type: "paragraph",
            content: [],
          },
        ]);
        // tiptap.commands.splitBlock();
        !tiptap.isDestroyed && selectInserted();
        tiptap.setOptions({ editable: true });
        setEditSubmitted(true);
        tiptap.commands.setTrackChangeStatus(false);
        setStreaming(false);
      },
      // onError (6th arg — historically called "onTimeout" but handles all stream errors)
      (errorPayload) => {
        handleStreamError(tiptap, editSessionIdRef.current, {
          captureException: Sentry.captureException,
          documentId: document.id,
        });
        // Show error to user — do NOT show Accept/Reject
        setEditError(getStreamErrorMessage(errorPayload));
        setStreaming(false);
      },
    );
  };

  const handleCloseReject = (e) => {
    e.preventDefault();
    didFinalizeChangesRef.current = true;

    try {
      tiptap.commands.rejectChangesBySession(editSessionIdRef.current);
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          documentId: document.id,
          func: "rejectChangesBySession",
        },
      });
    }

    dismiss();
  };

  const handleCloseAccept = (e) => {
    e.preventDefault();
    didFinalizeChangesRef.current = true;

    try {
      tiptap.commands.acceptChangesBySession(editSessionIdRef.current);
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          documentId: document.id,
          func: "acceptChangesBySession",
        },
      });
    }

    dismiss();
  };

  const handleSubmit = (e) => {
    handleSubmitEdit(e);
  };

  return !node
    ? null
    : ReactDOM.createPortal(
      <StyleSheetManager target={domHost}>
        <Container
          style={overlayStyle}
          id="command-menu"
          ref={containerRef}
        >
          <IconButton
            src="/assets/close.svg"
            className="close"
            onClick={handleCloseReject}
            aria-label="Close command menu"
            disabled={streaming}
          />

          {!editSubmitted && (
            <div className="command-menu-options">
              <div className={classNames("high-quality-switch", { disabled: streaming })}>
                <Toggle
                  checked={highQuality}
                  set={setHighQuality}
                />
                <div className="label-container">
                  <div className="main-label">Higher Quality</div>
                  <div className={classNames("sub-label", { active: highQuality })}>{highQuality ? "Uses credits" : "For complex tasks"}</div>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            {editSubmitted &&
              instructions.map((edit, index) => (
                <div
                  className="quick-edit-instruction"
                  key={index}
                >
                  {edit}
                </div>
              ))}
            {editError && (
              <div className="quick-edit-error" role="alert">
                {editError}
              </div>
            )}
            <div className="quick-actions">
              {!editSubmitted && (
                <TextareaAutosize
                  aria-label={"Enter your instructions for quick edit"}
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  ref={inputRef}
                  placeholder={"Tell Sudowrite how to edit your selection"}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (e.shiftKey) {
                        // Allow default behavior for Shift+Enter (new line)
                        return;
                      }
                      if (instruction.trim().length > 0) {
                        handleSubmit(e);
                      }
                    } else if (e.key === "Escape") {
                      handleCloseReject(e);
                    }
                  }}
                />
              )}

              {editSubmitted && (
                <TextareaAutosize
                  ref={followUpRef}
                  aria-label="Enter follow up instructions"
                  type="text"
                  className={classNames("follow-up", { disabled: streaming })}
                  value={followUp}
                  onChange={(e) => setFollowUp(e.target.value)}
                  placeholder="Write follow up instructions"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      handleCloseAccept(e);
                    } else if (e.key === "Escape") {
                      handleCloseReject(e);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (followUp) {
                        handleSubmitEdit(e);
                      }
                    }
                  }}
                />
              )}

              {(!editSubmitted || followUp || streaming) && <QuickEditButton disabled={streaming} />}

              {!streaming && editSubmitted && !followUp && (
                <EditChoices
                  handleCloseAccept={handleCloseAccept}
                  handleCloseReject={handleCloseReject}
                  invocationId={inovcationRef.current}
                />
              )}
            </div>
          </form>
        </Container>
      </StyleSheetManager>,
      node,
    );
};

export default CommandMenu;
